import NumerousLogo from "@/components/logo/NumerousLogo";
import { TypographyBase } from "@/components/typography/Base";
import { TypographyH4 } from "@/components/typography/H4";
import { TypographyMuted } from "@/components/typography/Muted";
import { TypographyXS } from "@/components/typography/XS";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Loading from "@/components/ui/loading";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { sendFeedback } from "@/lib/sentry/sendFeedback";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { FormEvent, useRef, useState } from "react";

function Feedback() {
  const [formIsValid, setFormIsValid] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const formRef = useRef<HTMLFormElement>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;

    setFeedbackError(false);
    setLoading(true);

    const formData = new FormData(form);
    const tempName = (formData.get("name") as string) || undefined;
    const tempEmail = (formData.get("email") as string) || undefined;
    const message = (formData.get("message") as string) || undefined;

    if (!tempName || !tempEmail || !message) return;
    setName(tempName);
    setEmail(tempEmail);

    sendFeedback({ name, email, message }, { includeReplay: false })
      .then(() => {
        setLoading(false);
        setFeedbackSent(true);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
        setFeedbackError(true);
      });
  }

  return (
    <div className="flex flex-col items-center justify-center w-full h-[calc(100vh - 12px)] bg-white dark:bg-brand-gray-950 rounded-lg dark:border dark:border-brand-gray-900 m-1.5">
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-2.5 gap-y-8">
        <NumerousLogo
          type="brandmark"
          size="lg"
          className="text-brand-primary"
        />
        <NumerousLogo
          type="wordmark"
          size="lg"
          className="text-brand-primary col-start-2"
        />

        <div className="col-start-2">
          <TypographyH4 className="text-brand-gray-900 dark:text-brand-gray-100">
            Help make Numerous better
          </TypographyH4>
          <TypographyMuted>
            Provide feedback, report bugs and help improve the NUmerous
            experience.
          </TypographyMuted>
          <Separator className="my-6" />
          <AnimatePresence mode="wait">
            {!feedbackSent && (
              <motion.form
                key="formElement"
                ref={formRef}
                onSubmit={handleSubmit}
                className="relative overflow-hidden px-0.5"
                style={{
                  width: "clamp(300px, 80vw, 600px)"
                }}
                initial={{ top: "8px", opacity: 0 }}
                animate={{ top: "0px", opacity: 1 }}
                exit={{ top: "8px", opacity: 0, height: "84px" }}
              >
                <Label htmlFor="name" className="text-brand-gray-600">
                  Name *
                </Label>
                <Input
                  onChange={() =>
                    formRef.current &&
                    setFormIsValid(formRef.current.checkValidity())
                  }
                  name="name"
                  id="name"
                  defaultValue={name}
                  required
                  autoFocus={!name}
                  className="mb-3"
                />
                <Label htmlFor="email" className="text-brand-gray-600">
                  Email *
                </Label>
                <Input
                  onChange={() =>
                    formRef.current &&
                    setFormIsValid(formRef.current.checkValidity())
                  }
                  name="email"
                  id="email"
                  defaultValue={email}
                  required
                  type="email"
                  className="mb-3"
                />
                <Label htmlFor="message" className="text-brand-gray-600">
                  Feedback *
                </Label>
                <Textarea
                  onChange={() =>
                    formRef.current &&
                    setFormIsValid(formRef.current.checkValidity())
                  }
                  name="message"
                  autoFocus={!!name}
                  required
                  className="min-h-32"
                />
                <div className="flex items-center justify-end mt-4 gap-3">
                  {feedbackError && (
                    <TypographyXS className="text-brand-error">
                      An error occurred while submitting your feedback. Please
                      try again or reload the page.
                    </TypographyXS>
                  )}
                  {loading && <Loading size="sm" />}
                  <Button
                    size="sm"
                    variant="secondary"
                    type="submit"
                    disabled={!formIsValid}
                  >
                    Submit
                  </Button>
                </div>
              </motion.form>
            )}
            {feedbackSent && (
              <motion.div
                key="submitCompleteContent"
                className="relative"
                style={{
                  width: "clamp(300px, 80vw, 600px)"
                }}
                initial={{ top: "8px", opacity: 0 }}
                animate={{ top: "0px", opacity: 1 }}
                exit={{ top: "8px", opacity: 0, height: "344px" }}
              >
                <TypographyBase className="dark:text-brand-gray-100">
                  Thank you for your feedback.
                </TypographyBase>
                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setFeedbackSent(false)}
                  >
                    Give more feedback
                  </Button>
                  <Link to="/">
                    <Button variant="outline">Go to Numerous →</Button>
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/feedback")({
  component: Feedback
});
